import { useEffect } from 'react'
import { useUIStore } from '@/stores'
import { useProject, useThread, useAvailableModels } from '@/hooks'
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Home, ChevronRight } from 'lucide-react'
import { getModelDisplayName } from '@/utils/helpers'
import { useT } from '@/i18n'

export function Header() {
  const {
    activeView, activeProjectId, activeThreadId,
    sidebarOpen, rightPanelOpen,
    selectedProvider, selectedModel,
    toggleSidebar, toggleRightPanel,
    setSelectedProvider, setSelectedModel,
    navigateToDashboard, navigateToProject,
  } = useUIStore()

  const t = useT()
  const { data: project } = useProject(activeProjectId)
  const { data: threadData } = useThread(activeThreadId)
  const { data: availableModels } = useAvailableModels()

  const availableProviders = availableModels || []

  const currentProvider = availableProviders.find((p) => p.provider_type === selectedProvider)
  const models = currentProvider?.models || []

  // Auto-select first available provider+model when list loads or current selection is unavailable
  useEffect(() => {
    if (!availableProviders.length) return
    // Empty string = notes mode; only auto-select if user hasn't explicitly set notes mode
    const userChoseNotes = localStorage.getItem('synapsios_notes_mode') === '1'
    if (selectedProvider === '' && userChoseNotes) return
    const providerValid = availableProviders.some(p => p.provider_type === selectedProvider)
    if (!providerValid) {
      const first = availableProviders[0]
      setSelectedProvider(first.provider_type)
      setSelectedModel(first.models[0] || '')
      return
    }
    // Provider ok, but check model
    const prov = availableProviders.find(p => p.provider_type === selectedProvider)
    if (prov && prov.models.length > 0 && !prov.models.includes(selectedModel)) {
      setSelectedModel(prov.models[0])
    }
  }, [availableModels])

  const handleProviderChange = (providerType: string) => {
    setSelectedProvider(providerType)
    if (providerType === '') {
      setSelectedModel('')
      localStorage.setItem('synapsios_notes_mode', '1')
      return
    }
    localStorage.removeItem('synapsios_notes_mode')
    const prov = availableProviders.find((p) => p.provider_type === providerType)
    if (prov && prov.models.length > 0) {
      setSelectedModel(prov.models[0])
    }
  }

  return (
    <header className="synapsios-header">
      <div className="header-left">
        <button className="header-icon-btn" onClick={toggleSidebar} title={sidebarOpen ? t.navHideSidebar : t.navShowSidebar}>
          {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
        </button>

        <div className="header-breadcrumb">
          <button className="breadcrumb-item" onClick={navigateToDashboard}>
            <Home size={14} />
          </button>
          {project && (
            <>
              <ChevronRight size={12} className="breadcrumb-sep" />
              <button className="breadcrumb-item" onClick={() => navigateToProject(project.id)}>
                <span className="breadcrumb-dot" style={{ backgroundColor: project.icon_color }} />
                {project.name}
              </button>
            </>
          )}
          {threadData && (
            <>
              <ChevronRight size={12} className="breadcrumb-sep" />
              <span className="breadcrumb-item current">{threadData.title}</span>
            </>
          )}
        </div>
      </div>

      <div className="header-center">
        {activeView === 'thread' && (
          <div className="model-selector-group">
            <select
              className="model-selector provider-select"
              value={selectedProvider}
              onChange={(e) => handleProviderChange(e.target.value)}
            >
              <option value="">Notatki (bez AI)</option>
              {availableProviders.map((p) => (
                <option key={p.provider_type} value={p.provider_type}>{p.display_name}</option>
              ))}
            </select>
            {selectedProvider !== '' && (
              <select
                className="model-selector model-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
              >
                {models.map((m) => (
                  <option key={m} value={m}>{getModelDisplayName(m)}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="header-right">
        {threadData && threadData.message_count !== undefined && (
          <span className="header-stat">{threadData.message_count} {t.msgs}</span>
        )}
        {activeView !== 'settings' && (
          <button className="header-icon-btn" onClick={toggleRightPanel} title={rightPanelOpen ? t.navHidePanel : t.navShowPanel}>
            {rightPanelOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
          </button>
        )}
      </div>
    </header>
  )
}
